<template>
  <main class="open-card-page">
    <AppNavBar
      :title="pageTitle"
      :auto-back="false"
      background-color="#f5f5f5"
      @click-left="handleBack"
    />

    <div v-if="!hideCardTypeSwitch" class="card-type-switch-wrap">
      <div class="card-type-switch">
        <button
          v-for="item in cardTypeTabs"
          :key="item.type"
          type="button"
          :class="{ active: activeCardType === item.type }"
          @click="switchCardType(item.type)"
        >
          {{ item.title }}
        </button>
      </div>
    </div>

    <van-skeleton
      v-if="cardOpenStore.loading"
      title
      :row="10"
      class="loading-card"
    />

    <template v-else-if="templates.length">
      <!-- <section class="card-course-notice">
        {{ courseGiftNotice }}
      </section> -->

      <section class="template-card card-carousel">
        <DesktopSwipe
          ref="templateSwipeRef"
          :loop="true"
          :show-indicators="false"
          @change="cardOpenStore.selectTemplate"
        >
          <van-swipe-item
            v-for="(item, index) in templates"
            :key="item.templateId || index"
            class="swipeItem"
          >
            <div class="template-preview">
              <app-image :src="cardBackground(item)" alt="" />
              <app-image
                class="template-preview__logo"
                :src="themeStore.image('img15', logo)"
                alt=""
              />
              <span v-if="item.bin" class="template-preview__bin"
                >BIN {{ item.bin }}</span
              >
            </div>
          </van-swipe-item>
        </DesktopSwipe>

        <div v-if="templates.length > 1" class="carousel-dots">
          <i
            v-for="(_, index) in templates"
            :key="index"
            :class="{ 'is-active': cardOpenStore.currentIndex === index }"
            @click="templateSwipeRef?.swipeTo?.(index)"
          />
        </div>

        <!-- <h2 class="template-name">{{ currentTemplate.name || "--" }}</h2>
        <p class="template-intro">{{ currentTemplate.intro || "" }}</p> -->

        <!-- <div class="dash-line carousel-divider" /> -->

        <div class="price-summary">
          <span>{{ t("card.openCardAmount") }}:</span>
          <div class="price-summary__value">
            {{ moneyText(cardOpenStore.totalOpenAmount) }}
            <span>{{ currentTemplate.openSymbol || "USDT" }}</span>
          </div>
        </div>
        <p v-if="showOpenAmountNotice" class="price-tip">
          {{ openAmountNotice }}
        </p>

        <div class="dash-line" />

        <div class="detail-rows">
          <div class="template-row">
            <span>{{ t("card.monthlyFee") }}</span>
            <div data-strong>0.00USD</div>
          </div>
          <div class="template-row">
            <span>{{ t("card.chargeFeeLabel") }}</span>
            <div data-strong>
              {{ formatFeeRate(currentTemplate.feeCharge) }}
            </div>
          </div>
          <div class="template-row">
            <span>{{ t("card.payFeeLabel") }}</span>
            <div data-strong>{{ formatFeeRate(currentTemplate.feePay) }}</div>
          </div>
          <div class="template-row">
            <span>{{ t("card.withdrawFeeLabel") }}</span>
            <div data-strong>
              {{ formatFeeRate(currentTemplate.feeWithdraw) }}
            </div>
          </div>
          <div class="template-row">
            <span>{{ t("card.monthlyCardUsageLimit") }}</span>
            <div data-strong>{{ limitText }}</div>
          </div>
          <div class="template-row">
            <span>{{ t("card.coreAdvantages") }}</span>
            <div data-strong>{{ currentTemplate.merits || "--" }}</div>
          </div>
          <div class="template-row">
            <span>{{ t("card.recommendedAudience") }}</span>
            <div data-strong>{{ currentTemplate.suitable || "--" }}</div>
          </div>
        </div>
      </section>

      <section class="scene-card">
        <header class="section-head section-head--action">
          <h2>{{ t("card.recentTradeScene") }}</h2>
          <button
            v-if="currentTemplate.hasScenes === true"
            type="button"
            class="section-link"
            @click="goAllPlatforms"
          >
            {{ t("card.viewAll") }}
            <van-icon name="arrow" size="14" color="var(--wp-color-brand-1)" />
          </button>
        </header>

        <div class="dash-line scene-card__divider" />

        <div
          v-for="(group, groupIndex) in visibleSupportSceneGroups"
          :key="group.id || group.label || groupIndex"
          class="scene-group"
        >
          <div v-if="groupIndex > 0" class="dash-line scene-group__divider" />
          <div class="scene-group__label">{{ group.label || "" }}</div>
          <div
            v-for="(item, itemIndex) in group.items"
            :key="item.id || item.name || itemIndex"
            class="scene-item"
          >
            <app-image class="scene-item__logo" :src="item.logo" alt="" />
            <span>{{ item.name || "" }}</span>
            <app-image
              class="scene-item__status"
              :src="item.supported === true ? checkYesIcon : checkNoIcon"
              alt=""
            />
          </div>
        </div>
      </section>

      <div class="submit-bar">
        <div class="submit-bar__content">
          <van-button
            round
            type="primary"
            :loading="cardOpenStore.submitting"
            @click="startOpenCard"
          >
            {{
              `${t("card.applyNow")} ${moneyText(cardOpenStore.totalOpenAmount)} ${currentTemplate.openSymbol || "USD"}`
            }}
          </van-button>
        </div>
      </div>
    </template>

    <section v-else class="empty-card">
      <van-empty :description="t('card.noCardTemplate')" image="search" />
      <van-button round type="primary" @click="loadData">{{
        t("common.retry")
      }}</van-button>
    </section>

    <PayPasswordPopup
      v-model:show="showPasswordDialog"
      v-model:password="passwordInput"
      :title="t('auth.verifyWalletPassword')"
      :placeholder="t('auth.enterPassword')"
      :confirm-text="t('common.confirm')"
      :forgot-text="t('auth.forgotPasswordRecovery')"
      show-forgot
      :confirm-disabled="!passwordInput.trim()"
      @cancel="closePasswordDialog(false)"
      @closed="handlePasswordPopupClosed"
      @forgot="handleForgotPassword"
      @confirm="submitPasswordDialog"
    />

    <van-popup
      v-model:show="showOpenCardPopup"
      round
      class="open-card-popup"
      @closed="handleOpenCardPopupClosed"
    >
      <section class="open-card-dialog">
        <header class="open-card-dialog__head">
          <div data-strong>{{ t("card.identityInfo") }}</div>
          <button type="button" @click="showOpenCardPopup = false">
            <app-image class="app-cancel-icon" :src="cancelIcon" alt="" />
          </button>
        </header>

        <div class="open-card-dialog__body">
          <label class="dialog-label">{{ t("card.lastName") }}</label>
          <van-field
            :model-value="capitalize(cardOpenStore.holderInfo?.lastName) || ''"
            readonly
          />

          <label class="dialog-label">{{ t("card.firstName") }}</label>
          <van-field
            :model-value="capitalize(cardOpenStore.holderInfo?.firstName) || ''"
            readonly
          />

          <div class="dialog-agreement">
            <!-- <button type="button" class="dialog-agreement__check">
              <van-icon size="20" :name="dialogAgreed ? 'passed' : 'circle'" />
            </button> -->
            <button
              type="button"
              class="dialog-agreement__check"
              @click="dialogAgreed = !dialogAgreed"
            >
              <AppSvgIcon
                v-if="dialogAgreed"
                name="icon"
                size="16"
                color="var(--wp-color-theme)"
              />
              <AppSvgIcon v-else name="icon" size="16" color="#C9CDD4" />
            </button>
            <p class="dialog-agreement__text">
              {{ t("card.agreementPrefix") }}
              <em role="button" tabindex="0" @click.stop="openPrivacyPolicy"
                >《{{ t("auth.privacyPolicy") }}》</em
              >
              {{ t("auth.and") }}
              <em role="button" tabindex="0" @click.stop="openUserAgreement"
                >《{{ t("auth.userAgreement") }}》</em
              >
            </p>
          </div>

          <van-button
            block
            round
            type="primary"
            :loading="cardOpenStore.submitting"
            @click="confirmOpenCard"
          >
            {{ t("card.payNow") }}
          </van-button>
        </div>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { showFailToast } from "vant";
import DesktopSwipe from "@/components/DesktopSwipe.vue";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import PayPasswordPopup from "@/components/PayPasswordPopup.vue";
import { payPwdCheck } from "@/api/auth";
import { cardMyList, getUserKyc } from "@/api/card";
import { useCardOpenStore } from "@/stores/card-open";
import { useCardOrderStore } from "@/stores/card-order";
import { useProfileStore } from "@/stores/profile";
import { useWebModalStore } from "@/stores/web-modal";
import { useThemeStore } from "@/stores/theme";
import { getDeviceId } from "@/utils/device";
import { safeBack } from "@/utils/navigation";
import { getRouteState, navigateWithState } from "@/utils/route-state";
import { formatFeeRate } from "@/view/card/utils";
import cardVisa from "@/assets/images/cardImage/card3_eg.png";
import cardMastercard from "@/assets/images/cardImage/card4_eg.png";
import logo from "@/assets/images/SVG/img15.png";
import cardThree from "@/assets/images/SVG/img16.png";
import nextBlueIcon from "@/assets/images/homeImage/next__blue_icon.png";
import cancelIcon from "@/assets/images/cancel_gray.png";
import checkYesIcon from "@/assets/images/check_yes.png";
import checkNoIcon from "@/assets/images/check_no.png";

const PRIVACY_POLICY_URLS = {
  "zh-CN": "https://winpay.com/privacy_policy_zh-CN.html",
  "en-US": "https://winpay.com/privacy_policy_en-US.html",
};
const USER_AGREEMENT_URLS = {
  "zh-CN": "https://api.winpay.dev/user_agreement_zh-CN.html",
  "en-US": "https://winpay.com/user_agreement_en-US.html",
};
const { t, locale } = useI18n();
const route = useRoute();
const router = useRouter();
const profileStore = useProfileStore();
const cardOpenStore = useCardOpenStore();
const templateSwipeRef = ref(null);
const cardOrderStore = useCardOrderStore();
const themeStore = useThemeStore();
const webModalStore = useWebModalStore();
const pageState = computed(() => getRouteState(route, "card-open", {}));

const showPasswordDialog = ref(false);
const showOpenCardPopup = ref(false);
const dialogAgreed = ref(true);
const passwordInput = ref("");
const activeCardType = ref(Number(pageState.value.type || 2));
let passwordResolver = null;
let openCardPopupClosedResolver = null;
const hideCardTypeSwitch = computed(() => Boolean(pageState.value.hideTab));
const pageTitle = computed(() =>
  activeCardType.value === 1
    ? t("card.applyCardTypeTwo")
    : t("card.applyCardTypeOne"),
);
const courseGiftNotice = computed(() =>
  t(
    activeCardType.value === 1
      ? "card.courseGiftNoticePhysical"
      : "card.courseGiftNoticeVirtual",
  ),
);
const isZhLocale = computed(() =>
  String(locale.value || "")
    .toLowerCase()
    .startsWith("zh"),
);
const privacyPolicyUrl = computed(
  () =>
    themeStore.url?.[
      `privacy_policy_${isZhLocale.value ? "zh-CN" : "en-US"}`
    ] ||
    (isZhLocale.value
      ? PRIVACY_POLICY_URLS["zh-CN"]
      : PRIVACY_POLICY_URLS["en-US"]),
);
const userAgreementUrl = computed(
  () =>
    themeStore.url?.[
      `user_agreement_${isZhLocale.value ? "zh-CN" : "en-US"}`
    ] ||
    (isZhLocale.value
      ? USER_AGREEMENT_URLS["zh-CN"]
      : USER_AGREEMENT_URLS["en-US"]),
);

const cardTypeTabs = computed(() => [
  { type: 2, title: t("card.virtualCard") },
  { type: 1, title: t("card.physicalCard") },
]);

function handleBack() {
  if (pageState.value.forceBackHome) {
    router.replace("/home/card");
    return;
  }

  safeBack(router);
}

const templates = computed(() => cardOpenStore.templates);
const currentTemplate = computed(() => cardOpenStore.currentTemplate);
const currentScene = computed(() => cardOpenStore.currentScene);
const limitText = computed(() => {
  const amount = currentTemplate.value.consumptionLimit;
  const unit = currentTemplate.value.unit || "";
  const symbol = currentTemplate.value.consumptionLimitSymbol || "";
  if (amount === null || amount === undefined || amount === "") {
    return "--";
  }
  return `${amount}${unit} ${symbol}`.trim();
});
const cardTypeText = computed(() => {
  if (activeCardType.value === 1) {
    return t("card.physicalCard");
  }
  return `${sceneName(currentScene.value)} ${t("card.prepaidCard")} ${currentTemplate.value.network || ""}`.trim();
});
const showOpenAmountNotice = computed(
  () =>
    activeCardType.value === 2 &&
    Number(currentTemplate.value.openChargeAmount || 0) > 0,
);
const openAmountNotice = computed(() => {
  return `${t("card.openFeeNoticePrefix")} ${moneyText(currentTemplate.value.openAmount)} ${currentTemplate.value.openSymbol || "USDT"}，${moneyText(currentTemplate.value.openChargeAmount)} ${currentTemplate.value.openSymbol || "USDT"} ${t("card.openFeeNoticeSuffix")}`;
});
const visibleSupportSceneGroups = computed(() => {
  const groups = Array.isArray(currentTemplate.value.supportSceneList)
    ? currentTemplate.value.supportSceneList
    : [];
  return groups.reduce((result, group) => {
    const items = (Array.isArray(group?.list) ? group.list : []).filter(
      (item) => item?.isShow === true,
    );
    if (items.length) {
      result.push({ ...group, items });
    }
    return result;
  }, []);
});

function sceneName(scene) {
  return scene?.sceneName || scene?.name || scene?.scene || "";
}

const capitalize = (str) => {
  // 非字符串/空值直接原样返回
  if (!str || typeof str !== "string") return str;
  const lowerStr = str.toLowerCase();
  return lowerStr.charAt(0).toUpperCase() + lowerStr.slice(1);
};

function moneyText(value) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount)) {
    return "0.0";
  }
  if (Number.isInteger(amount)) {
    return amount.toFixed(1);
  }
  return amount.toFixed(2).replace(/0$/, "");
}

function cardBackground(item) {
  if (String(item?.image || "").trim()) {
    return item.image;
  }
  if (activeCardType.value === 1) {
    return themeStore.image("img16", cardThree);
  }
  return String(item?.network || "").toLowerCase() === "visa"
    ? cardVisa
    : cardMastercard;
}

function switchCardType(type) {
  const nextType = Number(type || 2);
  if (nextType === activeCardType.value) return;
  activeCardType.value = nextType;
  navigateWithState(router, "/wallet/open-card", {
    namespace: "card-open",
    replace: true,
    payload: {
      ...pageState.value,
      type: String(nextType),
    },
  });
}

function openPrivacyPolicy() {
  webModalStore.open({
    url: privacyPolicyUrl.value,
    title: t("auth.privacyPolicy"),
    navBackgroundColor: "#fff",
  });
}

function openUserAgreement() {
  webModalStore.open({
    url: userAgreementUrl.value,
    title: t("auth.userAgreement"),
    navBackgroundColor: "#fff",
  });
}

function goAllPlatforms() {
  navigateWithState(router, "/wallet/open-card/platforms", {
    namespace: "card-platforms",
    payload: {
      supportSceneList: currentTemplate.value.supportSceneList || [],
    },
  });
}

function verifyWalletPassword() {
  passwordInput.value = "";
  showPasswordDialog.value = true;
  return new Promise((resolve) => {
    passwordResolver = resolve;
  });
}

function closePasswordDialog(result = false) {
  if (passwordResolver) {
    passwordResolver(result);
    passwordResolver = null;
  }
  showPasswordDialog.value = false;
}

function handlePasswordPopupClosed() {
  passwordInput.value = "";
}

function handleOpenCardPopupClosed() {
  openCardPopupClosedResolver?.();
  openCardPopupClosedResolver = null;
}

function closeOpenCardPopup() {
  if (!showOpenCardPopup.value) {
    return Promise.resolve();
  }

  const closed = new Promise((resolve) => {
    openCardPopupClosedResolver = resolve;
  });
  showOpenCardPopup.value = false;
  return closed;
}

function handleForgotPassword() {
  closePasswordDialog(false);
  router.push("/auth/forgot-pay-password");
}

async function submitPasswordDialog() {
  const payPwd = String(passwordInput.value || "").trim();
  if (!payPwd) {
    showFailToast(t("auth.enterPassword"));
    return;
  }

  const result = await payPwdCheck({ payPwd });
  if (!result) {
    showFailToast(t("auth.passwordError"));
    return;
  }

  closePasswordDialog(true);
}

async function loadData() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  try {
    await cardOpenStore.load({
      address: profileStore.platformAddress,
      cardType: activeCardType.value || 2,
    });
  } catch (error) {
    showFailToast(error.msg || t("common.retryLater"));
  }
}

async function startOpenCard() {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  if (Number(cardOpenStore.holderStatus) !== 3) {
    const holderStatus = Number(cardOpenStore.holderStatus || 0);
    const rejectReason = String(cardOpenStore.rejectReason || "").trim();
    if (
      holderStatus === 2 ||
      holderStatus === 4 ||
      (holderStatus === 1 && rejectReason)
    ) {
      navigateWithState(router, "/card/kyc/result", {
        namespace: "card-kyc-result",
        payload: {
          holderStatus: String(holderStatus || 1),
          reason: rejectReason,
          type: String(activeCardType.value || 2),
        },
      });
      return;
    }
    router.push("/card/kyc");
    return;
  }

  if (!currentTemplate.value.templateId) {
    showFailToast(t("card.selectCardTemplate"));
    return;
  }

  if (
    !(cardOpenStore.holderInfo?.firstName || "").trim() ||
    !(cardOpenStore.holderInfo?.lastName || "").trim()
  ) {
    showFailToast(t("card.identityInfoEmpty"));
    return;
  }

  if (activeCardType.value === 1) {
    try {
      const result = await cardMyList({
        address: profileStore.platformAddress,
        pageNum: 1,
        pageSize: 999,
        deviceToken: getDeviceId(),
        cardType: 2,
      });
      const virtualCards = (
        Array.isArray(result?.list) ? result.list : []
      ).filter((item) => item?.physical === false);
      cardOrderStore.mergeDraft({
        template: currentTemplate.value,
        templateId: String(currentTemplate.value.templateId || ""),
        cardPrice: String(moneyText(cardOpenStore.totalOpenAmount)),
        cardPriceSymbol: currentTemplate.value.openSymbol || "USD",
        virtualCardData: virtualCards,
        currentCardInfo: virtualCards[0] || {},
      });
      router.push("/card/physical-order/address");
      return;
    } catch (error) {
      showFailToast(error?.message || error?.msg || t("common.retryLater"));
      return;
    }
  }

  dialogAgreed.value = true;
  showOpenCardPopup.value = true;
}

async function confirmOpenCard() {
  if (!dialogAgreed.value) {
    showFailToast(t("card.agreeFirst"));
    return;
  }
  await closeOpenCardPopup();
  const verified = await verifyWalletPassword();
  if (!verified) {
    return;
  }

  try {
    await cardOpenStore.submit({
      address: profileStore.platformAddress,
      pcbId: currentTemplate.value.pcbId,
    });
    showOpenCardPopup.value = false;
    showPassedToast(t("card.openCardSuccess"));
    navigateWithState(router, "/card/index", {
      namespace: "home-card",
      replace: true,
      payload: {
        tab: "virtual",
        leftArrow: true,
        backTo: "/home/card",
      },
    });
  } catch (error) {
    if (Number(error?.code || 0) === 1500034) {
      try {
        await getUserKyc({ address: profileStore.platformAddress });
      } catch (_) {
        // ignore and continue to KYC flow
      }
      showOpenCardPopup.value = false;
      router.push("/card/kyc");
      return;
    }
  }
}

watch(
  () => [activeCardType.value, profileStore.platformAddress],
  () => {
    loadData();
  },
  { immediate: true },
);

watch(
  () => Number(pageState.value.type || 2),
  (value) => {
    if (activeCardType.value !== value) {
      activeCardType.value = value;
    }
  },
  { immediate: true },
);
</script>

<style scoped lang="scss">
.open-card-page {
  height: var(--app-viewport-height);
  min-height: var(--app-viewport-height);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 12px 0px calc(112px + env(safe-area-inset-bottom));
  background: #f5f5f5;
}

.card-type-switch-wrap {
  position: sticky;
  top: calc(var(--van-nav-bar-height) + env(safe-area-inset-top) - 12px);
  z-index: 20;
  margin: 0px;
  padding: 5px 14px;
  background-color: inherit;
  margin-bottom: 15px;
  border-bottom: 1px solid #f7f8fa;
  // border-bottom: 1px solid #fff;
}

.card-type-switch {
  display: flex;
  padding: 3px;
  border-radius: 30px;
  background: #eaeaea;

  button {
    flex: 1;
    padding: 8px 12px;
    border: 0;
    border-radius: 30px;
    background: transparent;
    color: #000000;
    font-size: 16px;
  }

  button.active {
    background: #ffffff;
  }
}

.template-card {
  border-radius: 16px;
  background: #ffffff;
  margin: 0 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.04);
}

.card-course-notice {
  margin: 0 16px 10px;
  padding: 10px 12px;
  border-radius: 12px;
  background: #e3e1fa;
  color: var(--wp-color-brand-1);
  font-size: 12px;
  line-height: 18px;
}

.loading-card,
.identity-card,
.scene-card,
.empty-card {
  margin-top: 10px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #ffffff;
}

.template-card {
  padding: 20px 16px;
}

.scene-card,
.identity-card {
  padding: 20px 16px 16px;
  margin: 16px 14px 0;
}

.card-carousel {
  :deep(.van-swipe) {
    margin: 0 -6px;
  }

  .swipeItem {
    box-sizing: border-box;
    padding: 0 6px;
  }
}

.template-preview {
  position: relative;
  width: 100%;
  aspect-ratio: 933 / 585;
  overflow: hidden;
  border-radius: 0;

  .app-image {
    display: block;
    width: 100%;
    height: 100%;

    :deep(.van-image__img) {
      object-fit: fill;
    }
  }
}

.template-preview__course-card {
  position: absolute;
  top: 24px;
  left: 28px;
  color: #ffffff;
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.template-preview__bin {
  position: absolute;
  top: 25px;
  right: 20px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
}

.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;

  i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d9d9d9;
  }

  .is-active {
    background: var(--wp-color-theme);
  }
}

.dash-line {
  width: 100%;
  height: 1px;
  margin-top: 17px;
  border-bottom: 1px dashed #eeeef6;
  // background-position: left top;
  // background-repeat: repeat-x;
  // background-size: 8px 1px;
}

.carousel-divider {
  margin-top: 24px;
}

.template-name {
  margin: 20px 0 17px;
  color: #000000;
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  text-align: center;
}

.template-intro {
  margin: 2px 0 0;
  color: #666666;
  font-size: 13px;
  line-height: 19px;
  text-align: center;
}

.price-summary {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 8px;
  margin: 21px 0 20px;

  > span {
    color: #000;
    font-size: 18px;
    font-weight: 600;
  }
}

.price-summary__value {
  color: var(--wp-color-theme);
  font-size: 18px;
  font-weight: 700;

  span {
    font-size: 16px;
    font-weight: 700;
  }
}

.price-tip {
  margin: 14px 0 0;
  font-size: 12px;
  color: #666;
  line-height: 18px;
}

.detail-rows {
  // margin-top: 15px;
}

.template-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 15px 0;
  border-bottom: 1px dashed #eeeef6;

  &:last-child {
    border-bottom: 0;
    padding-bottom: 0;
  }

  > span {
    flex: 0 0 92px;
    color: #333333;
    font-size: 14px;
    line-height: 20px;
  }

  > [data-strong] {
    flex: 1;
    min-width: 0;
    color: #000000;
    font-size: 16px;
    line-height: 22px;
    font-weight: 500;
    text-align: right;
  }
}

.template-preview__logo {
  position: absolute;
  top: 24px;
  left: 20px;
  width: 146px !important;
  height: auto !important;
}

.section-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 16px;
    font-weight: 700;
  }

  span {
    color: #999999;
    font-size: 12px;
    line-height: 18px;
    text-align: right;
  }
}

.section-head--action {
  align-items: center;
}

.section-link {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--wp-color-theme);
  font-size: 12px;

  .van-icon {
    font-weight: 700;
    -webkit-text-stroke: 0.4px currentColor;
  }

  .app-image {
    width: 14px;
    height: 14px;
  }
}

.identity-card {
  :deep(.van-cell) {
    padding: 12px 0;
  }

  :deep(.van-cell::after) {
    left: 0;
    right: 0;
  }

  :deep(.van-field__label) {
    width: 90px;
    color: #667085;
  }
}

.scene-card__divider,
.scene-group__divider {
  margin-top: 16px;
  margin-bottom: 16px;
}

.scene-group__label {
  color: #333333;
  font-size: 14px;
  line-height: 20px;
}

.scene-item {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;

  > span {
    flex: 1;
    min-width: 0;
    color: #000000;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }
}

.scene-item__logo {
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.scene-item__status {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  object-fit: contain;
}

.agreement-row {
  display: flex;
  align-items: flex-start;
  margin-top: 12px;
}

.agreement-row--shaking {
  animation: agreement-shake 360ms ease-in-out;
}

.submit-bar {
  position: fixed;
  right: 0;
  bottom: 0;
  left: 0;
  padding: 12px 14px calc(24px + env(safe-area-inset-bottom));
  background: #ffffff;
  box-shadow: 0 -4px 7.5px rgba(0, 0, 0, 0.12);

  &__content {
    display: grid;
    gap: 12px;
  }

  :deep(.van-button) {
    width: 100%;
    height: 48px;
    padding: 0 18px;
    font-weight: bold;
    box-shadow: 0 4px 8px 0 (0, 0, 0, 0.04);
  }
}

.open-card-popup {
  width: calc(100vw - 48px);
  max-width: 360px;
  overflow: hidden;
  background: transparent;
}

.open-card-dialog {
  padding: 20px 16px 18px;
  border-radius: 16px;
  background: linear-gradient(
    180deg,
    var(--wp-color-shadow-1) 3.43%,
    #ffffff 17.82%
  );
}

.open-card-dialog__head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 44px;

  [data-strong] {
    color: #111111;
    font-size: 18px;
    font-weight: 700;
    line-height: 22px;
  }

  button {
    position: absolute;
    top: 50%;
    right: 0px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #999999;
    transform: translateY(-50%);

    .app-image {
      width: 18px;
      height: 18px;
    }
  }
}

.open-card-dialog__body {
  margin-top: 12px;

  :deep(.van-field) {
    padding: 0;
    border-radius: 10px;
    background: #f7f8fa;
  }

  :deep(.van-field__control) {
    min-height: 44px;
    padding: 0 14px;
  }
}

.dialog-label {
  display: block;
  margin: 12px 0 8px;
  color: #333333;
  font-size: 14px;
  line-height: 20px;
}

.dialog-agreement {
  display: flex;
  align-items: center;
  margin: 16px 0 20px;

  &__check {
    flex: 0 0 auto;
    padding: 10px 2px 10px 0;
    border: none;
    background: transparent;
    cursor: pointer;
    margin-right: 6px;

    .app-image {
      display: block;
      width: 16px;
      height: 16px;
    }

    .app-svg-icon {
      display: block;
      vertical-align: initial;
    }
  }

  &__text {
    flex: 1;
    margin: 0;
    color: #1d2129;
    font-size: 12px;
    line-height: 18px;

    em {
      color: var(--wp-color-theme);
      font-style: normal;
      cursor: pointer;
    }
  }
}

.empty-card {
  margin-top: 12px;
  padding: 18px 16px 24px;
  text-align: center;
}
</style>
